import React, { useState } from 'react';
import { ShieldCheck, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Link } from 'react-router-dom';

/** Renders any registry document as a short, readable consent prompt. */
export const ConsentPrompt = ({ doc, open, onOpenChange, onAccept, details, reason, busy }) => {
  const [agreed, setAgreed] = useState(false);

  if (!doc) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setAgreed(false); onOpenChange(false); } }}>
      <DialogContent className="rounded-2xl max-w-lg w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto" data-testid="consent-prompt">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600" /> {doc.title}
          </DialogTitle>
          <DialogDescription>{doc.summary}</DialogDescription>
        </DialogHeader>

        {reason === 'details_changed' && (
          <p className="text-xs text-[#9A3412] bg-[#FFF7F5] border border-[#FBD9D2] rounded-xl px-3 py-2.5" data-testid="consent-changed-notice">
            The arranged details have changed since you gave permission. Please review and confirm the
            updated details below.
          </p>
        )}

        {reason === 'withdrawn' && (
          <p className="text-xs text-[#9A3412] bg-[#FFF7F5] border border-[#FBD9D2] rounded-xl px-3 py-2.5" data-testid="consent-withdrawn-notice">
            You withdrew this permission earlier. Give it again below to continue with this activity.
          </p>
        )}

        {details && (
          <div className="rounded-xl bg-[#F9FAFB] p-3.5 space-y-1.5" data-testid="consent-details">
            {details.map(([label, value]) => value ? (
              <div key={label} className="flex flex-col sm:flex-row sm:gap-2 text-sm">
                <span className="text-[#9CA3AF] sm:w-32 shrink-0">{label}</span>
                <span className="text-[#1F2937] break-words">{value}</span>
              </div>
            ) : null)}
          </div>
        )}

        <div className="space-y-2.5">
          {(doc.sections || []).map(s => (
            <div key={s.heading}>
              <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF]">{s.heading}</p>
              <p className="text-sm text-[#4B5563] mt-0.5 leading-relaxed">{s.body}</p>
            </div>
          ))}
          {(doc.points || []).map(p => (
            <p key={p} className="text-sm text-[#4B5563] leading-relaxed">• {p}</p>
          ))}
        </div>

        {doc.link && (
          <Link to={doc.link} target="_blank" className="text-xs text-[#F97D6A] font-semibold inline-flex items-center gap-1" data-testid="consent-doc-link">
            Read the full details <ExternalLink className="h-3 w-3" />
          </Link>
        )}

        <label className="flex items-start gap-2.5 cursor-pointer bg-white border border-[#E5E7EB] rounded-xl p-3">
          <Checkbox checked={agreed} onCheckedChange={setAgreed} className="mt-0.5" data-testid="consent-agree-checkbox" />
          <span className="text-sm text-[#1F2937] leading-relaxed">{doc.statement}</span>
        </label>

        <DialogFooter>
          <Button variant="outline" onClick={() => { setAgreed(false); onOpenChange(false); }} className="rounded-xl h-11 sm:h-10">
            Not now
          </Button>
          <Button
            disabled={!agreed || busy}
            onClick={async () => { await onAccept(); setAgreed(false); }}
            className="rounded-xl h-11 sm:h-10 bg-[#F97D6A] hover:bg-[#e86c5a] text-white"
            data-testid="consent-accept-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Give permission'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConsentPrompt;
